const translations = {
  en: {
    common: {
      cancel: "Cancel",
      close: "Close",
      next: "Next",
      confirm: "Confirm",
      save: "Save",
      create: "Create",
      email: "Email",
      password: "Password",
      role: "Role",
      updated: "Updated",
      scale: "Scale",
      themeSaved: "Theme saved",
      scaleSaveFailed: "Failed to save scale",
      themeSaveFailed: "Failed to save theme",
      language: "Language",
      security: "Security",
      enabled: "Enabled",
      disabled: "Disabled",
      unsavedChanges: "You have unsaved changes",
      cancelHint: "Discard unsaved changes and restore the last saved values",
      saveHint: "Save current changes",
      noChanges: "No changes to save",
    },
    login: {
      signIn: "Sign in",
      forgotPassword: "Reset password",
      back: "Back",
      emailNotFound: "Email address not found",
      invalidPassword: "Invalid password",
      resetCode: "Reset code",
      newPassword: "New password",
      repeatPassword: "Repeat password",
      passwordChanged: "Password changed",
      invalidResetCode: "Reset code is invalid",
      passwordsMismatch: "Passwords do not match",
      rememberMe: "Remember me",
      twoFactorPrompt: "Enter the confirmation code from your email",
      resendCode: "Send code again",
      resendCodeIn: "Resend available in {minutes}:{seconds}",
      verifyingCode: "Verifying code...",
      codeSent: "Verification code sent",
    },
    shell: {
      actionBar: "Action bar",
      skeleton: "Skeleton",
      add: "Add",
      edit: "Edit",
      delete: "Delete",
      pageTitle: "Application starter",
      pageText: "Login, dark mode, password recovery, user admin, header, footer, and a generic action bar are already in place",
      account: "Account",
      accountMenu: "Account menu",
      moduleA: "Module A",
      moduleAText: "Use this area for lists, dashboards, or tables",
      moduleB: "Module B",
      moduleBText: "This block is intentionally neutral, with no domain-specific surveillance logic",
      moduleC: "Module C",
      moduleCText: "A good starting point for a new product",
      stub: "stub",
      changeTheme: "Toggle theme",
      help: "Help",
      changePassword: "Change password",
      openSettings: "Open settings",
      signOut: "Sign out",
      profiles: "User profiles",
      profilesText: "Profile list with address, status, traffic usage, and sharing",
      profileName: "Profile name",
      displayName: "Display name",
      humanName: "User name",
      ipAddress: "Local IP",
      traffic: "Transferred",
      statusOnline: "online",
      statusOffline: "offline",
      downloadProfile: "Download profile",
      shareProfile: "Share profile",
      shareProfileSuccess: "Share link copied",
      shareProfileFailed: "Failed to create share link",
      profileNameWarning: "Do not use a nickname here, this name will be visible to the user",
      shareExpiresIn: "This link will expire in {time}",
      shareExpired: "Link expired",
      createProfile: "Create profile",
      editProfile: "Edit profile",
      deleteProfile: "Delete profile",
      noProfiles: "No profiles yet",
      createProfileSuccess: "Profile created",
      createProfileFailed: "Failed to create profile",
      updateProfileSuccess: "Profile updated",
      updateProfileFailed: "Failed to update profile",
      deleteProfileSuccess: "Profile deleted",
      deleteProfileFailed: "Failed to delete profile",
      downloadProfileFailed: "Failed to download profile",
      fillProfileName: "Enter a profile name",
      deleteProfileConfirm: "Delete this profile?",
      deleteProfileText: "This action removes the profile and its config",
      externalIp: "External IP",
      location: "Location",
      locationUnavailable: "Location unavailable",
      events: "Events",
      viewEvents: "View events",
      connectionEvents: "Connection events",
      noEvents: "No events yet",
      eventsLoadFailed: "Failed to load profile events",
      profileCreateSubtitle: "Create a new user profile",
      dashboardProfiles: "Total profiles",
      dashboardOnline: "Profiles online now",
      dashboardTraffic: "Current channel usage",
      dashboardYourIp: "Your current IP",
      last24Hours: "Last 24 hours",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      period: "Period",
    },
    settings: {
      general: "General",
      generalText: "Default language and theme for the login form and new users",
      account: "Account",
      accountText: "Account profile, email, avatar, and password management live here",
      accountSaved: "Account settings saved",
      accountSaveFailed: "Failed to save account settings",
      displayName: "Display name",
      uploadAvatar: "Upload avatar",
      removeAvatar: "Remove avatar",
      passwordSection: "Password",
      passwordSectionText: "Change the password for your current account",
      users: "Users",
      security: "Security",
      securityText: "Configure sign-in persistence and session lifetime",
      securitySaved: "Security settings saved",
      securitySaveFailed: "Failed to save security settings",
      allowRememberMe: "Allow users to stay signed in",
      allowRememberMeHint: "When enabled, users can choose to keep their sign-in between browser sessions",
      sessionDuration: "Session duration",
      sessionDurationField: "Duration in minutes",
      sessionDurationHint: "How long the session stays active when persistent sign-in is disabled",
      sessionDurationDisabled: "While persistent sign-in is enabled, the short session duration is only used when the user does not choose to stay signed in",
    },
    admin: {
      title: "Settings",
      subtitle: "Manage users and access roles",
      users: "Users",
      usersText: "User list, role changes, and password reset",
      lastLogin: "Last sign in",
      admin: "Administrator",
      user: "User",
      noUsers: "No users found",
      newPassword: "New password",
      changePassword: "Change password",
      addUser: "Add user",
      addUserText: "Create a new account and assign a role immediately",
      repeatPassword: "Repeat password",
      createUser: "Create user",
      roleUpdated: "User role updated",
      roleUpdateFailed: "Failed to update role",
      passwordUpdated: "User password updated",
      passwordUpdateFailed: "Failed to update user password",
      userCreated: "User created",
      userCreateFailed: "Failed to create user",
      fillEmailPassword: "Fill in email and password",
      passwordsMismatch: "Passwords do not match",
      usersLoadFailed: "Failed to load users",
      rememberMeVisibility: "Show 'Remember me' on login",
      allowRememberMe: "Allow user to keep signed in",
      securityUpdated: "Security settings updated",
      securityUpdateFailed: "Failed to update security settings",
      newUser: "New user",
      deleteUser: "Delete user",
      deleteUserConfirm: "Delete this user?",
      userDeleted: "User deleted",
      userDeleteFailed: "Failed to delete user",
      cannotDeleteSelf: "You cannot delete your own account",
      cannotChangeOwnPasswordHere: "Change your own password from the account settings",
      cannotChangeOwnRole: "You cannot change your own role",
      cannotDeleteOwnAccount: "You cannot delete your own account",
      passwordDialogText: "Set a new password for this account",
      deleteDialogText: "This action will permanently remove the user account",
    },
    password: {
      title: "Change password",
      action: "Change password",
      current: "Current password",
      new: "New password",
      repeat: "Repeat new password",
      confirmTitle: "Confirm password change",
      confirmText: "Confirm password change?",
      changed: "Password changed",
      changeFailed: "Failed to change password",
      currentIncorrect: "Current password is incorrect",
    },
    language: {
      ru: "Русский",
      en: "English",
    },
  },
  ru: {
    common: {
      cancel: "Отмена",
      close: "Закрыть",
      next: "Далее",
      confirm: "Подтвердить",
      save: "Сохранить",
      create: "Создать",
      email: "Эл.почта",
      password: "Пароль",
      role: "Роль",
      updated: "Обновлен",
      scale: "Масштаб",
      themeSaved: "Тема сохранена",
      scaleSaveFailed: "Не удалось сохранить масштаб",
      themeSaveFailed: "Не удалось сохранить тему",
      language: "Язык",
      security: "Безопасность",
      enabled: "Включено",
      disabled: "Выключено",
      unsavedChanges: "Есть несохранённые изменения",
      cancelHint: "Отменить несохранённые изменения и вернуть последние сохранённые значения",
      saveHint: "Сохранить текущие изменения",
      noChanges: "Нет изменений для сохранения",
    },
    login: {
      signIn: "Вход",
      forgotPassword: "Восстановить пароль",
      back: "Назад",
      emailNotFound: "Адрес электронной почты не найден",
      invalidPassword: "Неверный пароль",
      resetCode: "Код восстановления",
      newPassword: "Новый пароль",
      repeatPassword: "Повторите пароль",
      passwordChanged: "Пароль изменен",
      invalidResetCode: "Код восстановления неверный",
      passwordsMismatch: "Введенные пароли не совпадают",
      rememberMe: "Запомнить меня",
      twoFactorPrompt: "Введите код подтверждения из письма",
      resendCode: "Отправить код повторно",
      resendCodeIn: "Повторная отправка через {minutes}:{seconds}",
      verifyingCode: "Проверяем код...",
      codeSent: "Код подтверждения отправлен",
    },
    shell: {
      actionBar: "Панель действий",
      skeleton: "Каркас",
      add: "Добавить",
      edit: "Изменить",
      delete: "Удалить",
      pageTitle: "Базовый каркас приложения",
      pageText: "Логин, темная тема, восстановление пароля, админка пользователей, хедер, футер и универсальная панель действий уже на месте",
      account: "Аккаунт",
      accountMenu: "Меню аккаунта",
      moduleA: "Модуль A",
      moduleAText: "Здесь можно разместить список сущностей, дашборд или таблицу",
      moduleB: "Модуль B",
      moduleBText: "Этот блок специально нейтральный, без предметной логики и лишней привязки к исходному проекту",
      moduleC: "Модуль C",
      moduleCText: "Подходит как стартовая точка для нового продукта",
      stub: "stub",
      changeTheme: "Сменить тему",
      help: "Справка",
      changePassword: "Сменить пароль",
      openSettings: "Настройки",
      signOut: "Выйти",
      profiles: "Пользовательские профили",
      profilesText: "Список профилей с адресом, статусом, трафиком и возможностью поделиться",
      profileName: "Имя профиля",
      displayName: "Отображаемое имя",
      humanName: "Имя пользователя",
      ipAddress: "Локальный IP",
      traffic: "Передано",
      statusOnline: "онлайн",
      statusOffline: "оффлайн",
      downloadProfile: "Скачать профиль",
      shareProfile: "Поделиться профилем",
      shareProfileSuccess: "Ссылка скопирована",
      shareProfileFailed: "Не удалось создать ссылку",
      profileNameWarning: "Не используйте здесь погоняло, это имя будет видно пользователю",
      shareExpiresIn: "Эта ссылка станет недействительной через {time}",
      shareExpired: "Ссылка истекла",
      createProfile: "Создать профиль",
      editProfile: "Редактировать профиль",
      deleteProfile: "Удалить профиль",
      noProfiles: "Профилей пока нет",
      createProfileSuccess: "Профиль создан",
      createProfileFailed: "Не удалось создать профиль",
      updateProfileSuccess: "Профиль обновлён",
      updateProfileFailed: "Не удалось обновить профиль",
      deleteProfileSuccess: "Профиль удалён",
      deleteProfileFailed: "Не удалось удалить профиль",
      downloadProfileFailed: "Не удалось скачать профиль",
      fillProfileName: "Укажи имя профиля",
      deleteProfileConfirm: "Удалить этот профиль?",
      deleteProfileText: "Это действие удалит профиль и его конфиг",
      externalIp: "Внешний IP",
      location: "Локация",
      locationUnavailable: "Локация недоступна",
      events: "События",
      viewEvents: "Просмотр событий",
      connectionEvents: "События подключений",
      noEvents: "Событий пока нет",
      eventsLoadFailed: "Не удалось загрузить события профиля",
      profileCreateSubtitle: "Создание нового пользовательского профиля",
      dashboardProfiles: "Всего профилей",
      dashboardOnline: "Профилей онлайн сейчас",
      dashboardTraffic: "Текущая загрузка канала",
      dashboardYourIp: "Ваш текущий IP",
      last24Hours: "Последние 24 часа",
      last7Days: "Последние 7 дней",
      last30Days: "Последние 30 дней",
      period: "Период",
    },
    settings: {
      general: "Основные",
      generalText: "Язык и тема по умолчанию для формы входа и новых пользователей",
      account: "Аккаунт",
      accountText: "Здесь находятся профиль аккаунта, email, аватар и управление паролем",
      accountSaved: "Настройки аккаунта сохранены",
      accountSaveFailed: "Не удалось сохранить настройки аккаунта",
      displayName: "Отображаемое имя",
      uploadAvatar: "Загрузить аватар",
      removeAvatar: "Удалить аватар",
      passwordSection: "Пароль",
      passwordSectionText: "Изменение пароля для текущего аккаунта",
      users: "Пользователи",
      security: "Безопасность",
      securityText: "Настройте сохранение входа и длительность сессии",
      securitySaved: "Настройки безопасности сохранены",
      securitySaveFailed: "Не удалось сохранить настройки безопасности",
      allowRememberMe: "Разрешить пользователям сохранять вход",
      allowRememberMeHint: "При активации параметра пользователю доступна опция сохранения входа на экране входа",
      sessionDuration: "Длительность сессии",
      sessionDurationField: "Время в минутах",
      sessionDurationHint: "Сколько живёт сессия, если сохранение входа не используется",
      sessionDurationDisabled: "При включённом сохранении входа короткая сессия используется только если пользователь сам не выбрал сохранение входа",
    },
    admin: {
      title: "Настройки",
      subtitle: "Управление пользователями и ролями доступа",
      users: "Пользователи",
      usersText: "Парамертры учетных записей, установка ролей и изменение пароля",
      lastLogin: "Последний вход",
      admin: "Администратор",
      user: "Пользователь",
      noUsers: "Пользователи не найдены",
      newPassword: "Новый пароль",
      changePassword: "Сменить пароль",
      addUser: "Добавить пользователя",
      addUserText: "Создайте новую учетную запись и сразу задайте ей роль",
      repeatPassword: "Повторите пароль",
      createUser: "Создать пользователя",
      roleUpdated: "Роль пользователя обновлена",
      roleUpdateFailed: "Не удалось обновить роль",
      passwordUpdated: "Пароль пользователя обновлен",
      passwordUpdateFailed: "Не удалось изменить пароль",
      userCreated: "Пользователь создан",
      userCreateFailed: "Не удалось создать пользователя",
      fillEmailPassword: "Заполните email и пароль",
      passwordsMismatch: "Пароли не совпадают",
      usersLoadFailed: "Не удалось загрузить пользователей",
      rememberMeVisibility: "Показывать 'Запомнить меня' на логине",
      allowRememberMe: "Разрешить пользователю сохранять вход",
      securityUpdated: "Настройки безопасности обновлены",
      securityUpdateFailed: "Не удалось обновить настройки безопасности",
      newUser: "Новый пользователь",
      deleteUser: "Удалить пользователя",
      deleteUserConfirm: "Удалить этого пользователя?",
      userDeleted: "Пользователь удалён",
      userDeleteFailed: "Не удалось удалить пользователя",
      cannotDeleteSelf: "Нельзя удалить собственную учётную запись",
      cannotChangeOwnPasswordHere: "Свой пароль меняется в настройках аккаунта",
      cannotChangeOwnRole: "Нельзя менять свою роль",
      cannotDeleteOwnAccount: "Нельзя удалить собственную учётную запись",
      passwordDialogText: "Задайте новый пароль для этой учётной записи",
      deleteDialogText: "Это действие безвозвратно удалит учётную запись пользователя",
    },
    password: {
      title: "Смена пароля",
      action: "Изменить пароль",
      current: "Текущий пароль",
      new: "Новый пароль",
      repeat: "Повторите новый пароль",
      confirmTitle: "Подтверждение смены пароля",
      confirmText: "Подтвердить изменение пароля?",
      changed: "Пароль изменен",
      changeFailed: "Ошибка при изменении пароля",
      currentIncorrect: "Текущий пароль введён неверно",
    },
    language: {
      ru: "Русский",
      en: "English",
    },
  },
};

export const DEFAULT_LOCALE = process.env.REACT_APP_DEFAULT_LOCALE || "ru";
export const SUPPORTED_LOCALES = ["ru", "en"];

export function t(key, locale = DEFAULT_LOCALE) {
  const dict = translations[locale] || translations.ru;
  return key.split(".").reduce((acc, part) => acc?.[part], dict) || key;
}
